import { ExperimentViewOverridesSchema } from '@/types/schema';

import { DatasetSessionCompareSettings, METRICS_COLUMNS } from '../constants';
import { CORE_COLUMNS, extractPrefixedKey } from './columnTypeUtils';
import { DEFAULT_METRIC_NAMES } from './columnTypeUtils';

export type ExperimentViewMetric =
  (typeof METRICS_COLUMNS)[keyof typeof METRICS_COLUMNS];

/**
 * Render functions interface for column override rendering (headers or cells)
 */
export interface ColumnRenderFunctions {
  renderInputsColumn: (
    columnName: string,
    columnIndex: number
  ) => React.ReactNode;
  renderAttachmentsColumn: (columnIndex: number) => React.ReactNode;
  renderReferenceOutputsColumn: (
    columnName: string,
    columnIndex: number
  ) => React.ReactNode;
  renderOutputsColumn: (columnIndex: number) => React.ReactNode;
  renderFeedbackColumn: (
    feedbackKey: string,
    columnIndex: number
  ) => React.ReactNode;
  renderMetricsColumn: (
    metric: ExperimentViewMetric,
    columnIndex: number
  ) => React.ReactNode;
  renderComputedColumn: (
    columnName: string,
    columnIndex: number
  ) => React.ReactNode;
}

/**
 * Filter column overrides based on visibility settings and validation
 */
function filterVisibleColumnOverrides(
  columnOverrides: ExperimentViewOverridesSchema['column_overrides'],
  displaySettings: Pick<
    DatasetSessionCompareSettings,
    | 'isReferenceInputHidden'
    | 'isAttachmentsHidden'
    | 'isReferenceOutputHidden'
    | 'hiddenFeedbackColumns'
    | 'hiddenMetricsColumns'
  >
): ExperimentViewOverridesSchema['column_overrides'] {
  const {
    isReferenceInputHidden,
    isAttachmentsHidden,
    isReferenceOutputHidden,
    hiddenFeedbackColumns,
    hiddenMetricsColumns,
  } = displaySettings;

  return columnOverrides.filter((column) => {
    const col = column.column;

    // Filter based on column configuration and visibility settings
    return !(
      column.hide ||
      (col.startsWith('metrics.') &&
        !METRICS_COLUMNS[extractPrefixedKey(col)]) ||
      (col === 'inputs' && isReferenceInputHidden) ||
      (col === 'attachments' && isAttachmentsHidden) ||
      (col === 'reference_outputs' && isReferenceOutputHidden) ||
      (col.startsWith('inputs.') && isReferenceInputHidden) ||
      (col.startsWith('reference_outputs.') && isReferenceOutputHidden) ||
      (col.startsWith('feedback.') &&
        hiddenFeedbackColumns.includes(extractPrefixedKey(col))) ||
      (col.startsWith('metrics.') &&
        hiddenMetricsColumns.includes(extractPrefixedKey(col)))
    );
  });
}

/**
 * Render a single column override using provided render functions
 */
function renderSingleColumnOverride(
  column: ExperimentViewOverridesSchema['column_overrides'][number],
  columnIndex: number,
  renderFunctions: ColumnRenderFunctions
): React.ReactNode {
  const {
    renderInputsColumn,
    renderAttachmentsColumn,
    renderReferenceOutputsColumn,
    renderOutputsColumn,
    renderFeedbackColumn,
    renderMetricsColumn,
    renderComputedColumn,
  } = renderFunctions;

  // Core column handlers
  const columnHandlers = new Map([
    ['inputs', () => renderInputsColumn(column.column, columnIndex)],
    ['attachments', () => renderAttachmentsColumn(columnIndex)],
    [
      'reference_outputs',
      () => renderReferenceOutputsColumn(column.column, columnIndex),
    ],
    ['outputs', () => renderOutputsColumn(columnIndex)],
  ]);

  const componentGenerator = columnHandlers.get(column.column);
  if (componentGenerator) {
    return componentGenerator();
  }

  // Specific sub-columns (inputs.*, outputs.*, reference_outputs.*)
  if (
    column.column.startsWith('inputs.') ||
    column.column.startsWith('outputs.') ||
    column.column.startsWith('reference_outputs.') ||
    column.column.startsWith('metadata.')
  ) {
    return renderComputedColumn(column.column, columnIndex);
  }

  // Feedback columns
  if (column.column.startsWith('feedback.')) {
    const feedbackKey = extractPrefixedKey(column.column);
    return renderFeedbackColumn(feedbackKey, columnIndex);
  }

  // Metrics columns
  if (column.column.startsWith('metrics.')) {
    const metricKey = extractPrefixedKey(column.column);
    const metric = METRICS_COLUMNS[metricKey];
    return metric ? renderMetricsColumn(metric, columnIndex) : null;
  }

  return null;
}

/**
 * Get the names of rendered columns from overrides, including additional columns
 */
export function getRenderedColumnNames(
  columnOverrides: ExperimentViewOverridesSchema['column_overrides'],
  displaySettings: Pick<
    DatasetSessionCompareSettings,
    | 'isReferenceInputHidden'
    | 'isAttachmentsHidden'
    | 'isReferenceOutputHidden'
    | 'hiddenFeedbackColumns'
    | 'hiddenMetricsColumns'
  >,
  aggregateFeedbackKeys: string[],
  showFeedbackAndMetricsColumns: boolean
): string[] {
  // Filter visible columns
  const visibleColumns = filterVisibleColumnOverrides(
    columnOverrides,
    displaySettings
  );

  // Get configured column names
  const configuredColumnNames = visibleColumns.map((column) => column.column);

  // Calculate additional columns not in config
  const additionalColumnNames: string[] = [];

  const coreColumns = CORE_COLUMNS.filter(
    (coreColumn) =>
      !displaySettings.isAttachmentsHidden || coreColumn !== 'attachments'
  );
  // Add missing core columns
  const missingCoreColumns = coreColumns.filter(
    (coreColumn) => !configuredColumnNames.includes(coreColumn)
  );
  additionalColumnNames.push(...missingCoreColumns);

  if (showFeedbackAndMetricsColumns) {
    const { hiddenFeedbackColumns, hiddenMetricsColumns } = displaySettings;

    // Get ALL feedback keys in config (including hidden ones)
    const allConfiguredFeedbackKeys = columnOverrides
      .filter((col) => col.column.startsWith('feedback.'))
      .map((col) => extractPrefixedKey(col.column));

    // Get ALL metrics keys in config (including hidden ones)
    const allConfiguredMetricsKeys = columnOverrides
      .filter((col) => col.column.startsWith('metrics.'))
      .map((col) => extractPrefixedKey(col.column));

    // Find additional feedback keys not in config at all
    const additionalFeedbackKeys = aggregateFeedbackKeys.filter(
      (key) =>
        !allConfiguredFeedbackKeys.includes(key) &&
        !hiddenFeedbackColumns.includes(key)
    );

    // Find additional metrics keys not in config at all
    const additionalMetricsKeys = DEFAULT_METRIC_NAMES.filter(
      (key) =>
        !allConfiguredMetricsKeys.includes(key) &&
        !hiddenMetricsColumns.includes(key)
    );

    // Add additional feedback column names
    additionalFeedbackKeys.forEach((key) => {
      additionalColumnNames.push(`feedback.${key}`);
    });

    // Add additional metrics column names
    additionalMetricsKeys.forEach((key) => {
      const metric = METRICS_COLUMNS[key];
      if (metric) {
        additionalColumnNames.push(`metrics.${key}`);
      }
    });
  }

  return [...configuredColumnNames, ...additionalColumnNames];
}

/**
 * Generate complete list of rendered columns from overrides, including additional headers
 */
export function renderOverriddenColumns(
  columnOverrides: ExperimentViewOverridesSchema['column_overrides'],
  displaySettings: Pick<
    DatasetSessionCompareSettings,
    | 'isReferenceInputHidden'
    | 'isAttachmentsHidden'
    | 'isReferenceOutputHidden'
    | 'hiddenFeedbackColumns'
    | 'hiddenMetricsColumns'
  >,
  renderFunctions: ColumnRenderFunctions,
  aggregateFeedbackKeys: string[],
  showFeedbackAndMetricsColumns: boolean
) {
  // Get all column names (configured + additional)
  const columnNames = getRenderedColumnNames(
    columnOverrides,
    displaySettings,
    aggregateFeedbackKeys,
    showFeedbackAndMetricsColumns
  );

  // Filter visible columns for configured ones
  const visibleColumns = filterVisibleColumnOverrides(
    columnOverrides,
    displaySettings
  );

  // Generate configured columns
  const configuredColumns = visibleColumns
    .map((column, i) => renderSingleColumnOverride(column, i, renderFunctions))
    .filter(Boolean);

  // Generate additional columns
  const additionalHeaders: React.ReactNode[] = [];
  const additionalColumnNames = columnNames.slice(visibleColumns.length);

  additionalColumnNames.forEach((columnName, index) => {
    const columnIndex = visibleColumns.length + index;

    if (columnName.startsWith('feedback.')) {
      const feedbackKey = extractPrefixedKey(columnName);
      const header = renderFunctions.renderFeedbackColumn(
        feedbackKey,
        columnIndex
      );
      if (header) additionalHeaders.push(header);
    } else if (columnName.startsWith('metrics.')) {
      const metricKey = extractPrefixedKey(columnName);
      const metric = METRICS_COLUMNS[metricKey];
      if (metric) {
        const header = renderFunctions.renderMetricsColumn(metric, columnIndex);
        if (header) additionalHeaders.push(header);
      }
    }
  });

  return [...configuredColumns, ...additionalHeaders];
}
